// Saved addresses on quotes and invoices (orders.*_address_snapshot) use the
// address-book field names: label, attention, street1, street2, city,
// stateCode, postalCode, countryCode. The client pages read older names
// (name, line1, state…), so only the city and ZIP showed (Stephanie
// 2026-10-13: "the billing and shipping address need to be listed in full").
// This reads either set and returns the lines in order.

type Snap = Record<string, unknown> | null | undefined;

function pick(a: Record<string, unknown>, ...keys: string[]): string | null {
  for (const k of keys) {
    const v = a[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

export interface AddressLines {
  /** Bold first line: company / label and attention, when present. */
  heading: string | null;
  lines: string[];
}

export function addressSnapshotLines(snapshot: Snap): AddressLines | null {
  if (!snapshot || typeof snapshot !== "object") return null;
  const a = snapshot;
  // The address's label ("Main") is internal — never shown to the client
  // (Stephanie 2026-10-14). Company on top, then "Attn: <contact>".
  const company = pick(a, "company", "companyName");
  const person = pick(a, "name", "attention", "contactName");
  const heading = company ?? person;
  const lines: string[] = [];
  if (company && person && company !== person) lines.push(`Attn: ${person}`);
  const street1 = pick(a, "street1", "line1", "addressLine1");
  const street2 = pick(a, "street2", "line2", "addressLine2");
  if (street1) lines.push(street1);
  if (street2) lines.push(street2);
  const city = pick(a, "city");
  const state = pick(a, "stateCode", "state");
  const zip = pick(a, "postalCode", "zip");
  const cityState = [city, state].filter(Boolean).join(", ");
  const cityLine = [cityState, zip].filter(Boolean).join(" ");
  if (cityLine) lines.push(cityLine);
  // US addresses don't need the country; anything else shows it.
  const country = pick(a, "countryCode", "country");
  if (country && !/^(US|USA|United States)$/i.test(country)) lines.push(country);
  if (!heading && lines.length === 0) return null;
  return { heading, lines };
}
